import * as React from 'react';
import { useEffect, useRef } from 'react';

import { cn } from '../lib/utils';

/** Static variant의 고정 높이 */
const STATIC_SIZE_HEIGHT = {
  sm: 70,
  md: 160,
  lg: 320,
} as const;

/** Dynamic variant의 기본/최대 높이 */
const DYNAMIC_DEFAULT_HEIGHT = 32;
const DYNAMIC_MAX_HEIGHT = 88;

type TextareaVariant = 'static' | 'dynamic';
type TextareaSize = 'sm' | 'md' | 'lg' | 'auto';

interface TextareaProps extends React.ComponentProps<'textarea'> {
  error?: boolean;
  label?: string;
  helpText?: string;
  /** 내장 글자 수 카운터 표시 여부. maxLength와 함께 사용. */
  showCounter?: boolean;
  /**
   * 카운터 분모(n/m 의 m)를 maxLength 와 분리해 표시할 때 사용.
   * 미지정 시 maxLength 사용. 입력 하드 블로킹(maxLength)보다 큰 "정책상 최대"를
   * 카운터에 보여줘야 할 때(예: per-note 최대는 1000 이지만 방 합산 여유로 실입력은 992 제한).
   */
  counterMax?: number;
  wrapperClassName?: string;
  /** Static: 고정 높이, Dynamic: 자동 확장 (default: 'static') */
  variant?: TextareaVariant;
  /** Static일 때 높이 크기, Dynamic일 때는 'auto' (default: variant에 따라 자동) */
  size?: TextareaSize;
  /** Dynamic variant 우측 액션 버튼 영역 (send, continue, ending 등) */
  rightSlot?: React.ReactNode;
  /** 하단 좌측 영역 (카운터와 같은 줄, TemplateTagButton 등) */
  bottomSlot?: React.ReactNode;
}

/** value에서 글자 수 계산 */
function getCharCount(
  value: string | number | readonly string[] | undefined
): number {
  if (typeof value === 'string') return value.length;
  if (typeof value === 'number') return String(value).length;
  return 0;
}

/** label + helpText + counter 공통 wrapper */
function TextareaFieldWrapper({
  label,
  helpText,
  error,
  showCounter,
  charCount,
  maxLength,
  counterMax,
  bottomSlot,
  wrapperClassName,
  children,
}: {
  label?: string;
  helpText?: string;
  error?: boolean;
  showCounter?: boolean;
  charCount: number;
  maxLength?: number;
  counterMax?: number;
  bottomSlot?: React.ReactNode;
  wrapperClassName?: string;
  children: React.ReactNode;
}) {
  const counterDenominator = counterMax ?? maxLength;
  const showBuiltinCounter = showCounter && counterDenominator !== undefined;
  const showBottomRow = !!helpText || showBuiltinCounter || !!bottomSlot;

  return (
    <div
      className={cn(
        'gap-global-spacing-8 inline-flex w-full flex-col items-start justify-start',
        wrapperClassName
      )}
    >
      {label && (
        <div className='gap-global-spacing-4 inline-flex items-center self-stretch'>
          <span className='text-text-neutral-solid text-sm leading-4 font-semibold'>
            {label}
          </span>
        </div>
      )}
      {children}
      {showBottomRow && (
        <div className='gap-global-spacing-10 px-global-spacing-4 inline-flex w-full items-start justify-between'>
          {bottomSlot ||
            (helpText && (
              <p
                className={cn(
                  'flex-1 text-sm leading-4',
                  error ? 'text-bg-error-solid' : 'text-text-neutral-weak'
                )}
                // 에러일 때만 알림으로 올린다 — 색만 빨개지면 스크린리더 사용자는
                // 무엇이 잘못됐는지 알 수 없다. 일반 안내(error=false)는 그냥 설명이라
                // 매번 읽어주면 오히려 방해된다.
                role={error ? 'alert' : undefined}
              >
                {helpText}
              </p>
            ))}
          {showBuiltinCounter && (
            <span className='text-text-neutral-subtle ms-auto text-end text-sm leading-4'>
              {charCount.toLocaleString()}/
              {counterDenominator!.toLocaleString()}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      error,
      label,
      helpText,
      showCounter,
      wrapperClassName,
      maxLength,
      counterMax,
      value,
      style,
      variant = 'static',
      size,
      rightSlot,
      bottomSlot,
      ...props
    },
    ref
  ) => {
    const isDynamic = variant === 'dynamic';

    // Auto-resize를 위한 내부 ref
    const internalRef = useRef<HTMLTextAreaElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        internalRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
      },
      [ref]
    );

    // Dynamic variant: 텍스트 양에 따라 높이 자동 조절
    useEffect(() => {
      if (!isDynamic || !internalRef.current) return;

      const textarea = internalRef.current;
      const currentValue = value !== undefined ? String(value) : textarea.value;

      if (!currentValue) {
        textarea.style.height = `${DYNAMIC_DEFAULT_HEIGHT}px`;
        return;
      }
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, DYNAMIC_MAX_HEIGHT)}px`;
    }, [isDynamic, value]);

    const charCount = getCharCount(value);

    // counterMax 는 항상 maxLength 와 함께 전달된다(분모 표시 override) — 카운터
    // 노출 판정은 maxLength 기준 유지(복잡도 억제). 분모 치환은 wrapper 가 담당.
    const showBuiltinCounter = showCounter && maxLength !== undefined;
    const showBottomRow = !!helpText || showBuiltinCounter || !!bottomSlot;
    const needsWrapper =
      !!label || showBottomRow || wrapperClassName !== undefined;

    // Static size별 고정 높이 — size가 명시된 경우에만 적용 (기존 className 높이 override 방지)
    const staticHeight =
      !isDynamic && size !== undefined && size !== 'auto'
        ? STATIC_SIZE_HEIGHT[size]
        : undefined;

    const textareaEl = (
      // dir="auto": 입력 콘텐츠가 제 첫 강한 문자 기준으로 방향을 잡게 한다(RTL UI 안의
      // 한국어/라틴 입력 정렬·커서 방지). {...props} 앞이라 호출자 dir 이 우선한다.
      // 비어 있으면 LTR 로 잡혀 아랍어 안내 문구 마침표가 앞에 붙으므로, 안내 문구가 보이는
      // 동안은 화면 방향(html dir)을 따른다(HD-1034).
      <textarea
        dir='auto'
        className={cn(
          isDynamic
            ? // Dynamic: 채팅 입력 스타일 — 외부 컨테이너가 border를 담당
              'text-text-neutral-solid placeholder:text-text-neutral-weak w-full flex-1 resize-none scrollbar-none overflow-y-auto bg-transparent text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50'
            : // Static: 폼 입력 스타일
              'rounded-global-radius-16 border-line-border-neutral-weak bg-bg-neutral-subtle px-global-spacing-12 py-global-spacing-8 text-text-neutral-solid placeholder:text-text-neutral-weak flex w-full resize-none border text-sm shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          'rtl:placeholder-shown:[direction:rtl]',
          !isDynamic &&
            (error
              ? 'border-line-border-error border-[1.5px] focus-visible:shadow-[0px_0px_0px_3px_rgba(255,29,32,0.20)]'
              : 'focus-visible:border-line-outline-brand-primary-focused focus-visible:shadow-[0px_0px_0px_3px_rgba(196,150,255,0.20)]'),
          className
        )}
        maxLength={maxLength}
        ref={setRefs}
        rows={isDynamic ? 1 : undefined}
        value={value}
        style={
          {
            ...(staticHeight ? { height: staticHeight } : {}),
            ...(isDynamic
              ? {
                  height: DYNAMIC_DEFAULT_HEIGHT,
                  maxHeight: DYNAMIC_MAX_HEIGHT,
                }
              : {}),
            // Android WebView Extract UI 방지 (전체 화면 키보드 모드 비활성화)
            imeOptions: 'flagNoExtractUi',
            ...style,
          } as React.CSSProperties
        }
        enterKeyHint='enter'
        {...props}
      />
    );

    // Dynamic variant: 컨테이너 + rightSlot 조합
    if (isDynamic) {
      const dynamicEl = (
        <div
          className={cn(
            'gap-global-spacing-4 rounded-global-radius-20 bg-bg-neutral-subtle py-global-spacing-4 ps-global-spacing-12 pe-global-spacing-4 border-line-border-neutral-weak flex items-end border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]',
            error && 'border-line-border-error'
          )}
        >
          {textareaEl}
          {rightSlot}
        </div>
      );

      if (!needsWrapper) return dynamicEl;

      return (
        <TextareaFieldWrapper
          label={label}
          helpText={helpText}
          error={error}
          showCounter={showCounter}
          charCount={charCount}
          maxLength={maxLength}
          counterMax={counterMax}
          bottomSlot={bottomSlot}
          wrapperClassName={wrapperClassName}
        >
          {dynamicEl}
        </TextareaFieldWrapper>
      );
    }

    // Static variant
    if (!needsWrapper) return textareaEl;

    return (
      <TextareaFieldWrapper
        label={label}
        helpText={helpText}
        error={error}
        showCounter={showCounter}
        charCount={charCount}
        maxLength={maxLength}
        counterMax={counterMax}
        bottomSlot={bottomSlot}
        wrapperClassName={wrapperClassName}
      >
        {textareaEl}
      </TextareaFieldWrapper>
    );
  }
);
Textarea.displayName = 'Textarea';

export { Textarea };
export type { TextareaProps };
